/**
 * Ticket VS1: Empfängerauswahl samt Vorschau. Diese Ansicht verschickt nichts
 * und speichert keinen Kreis – sie zeigt nur, wen die Auswahl trifft und wer
 * herausfällt. Entschieden wird ausschließlich in `buildRecipientSelection`,
 * hier steht keine eigene Auswahl (siehe Vermerk in src/lib/recipients.ts).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { adminInputClass } from "@/components/admin/AdminField";
import { listContactSources } from "@/lib/contact-sources.functions";
import { listContactTags } from "@/lib/contacts.functions";
import { previewRecipients } from "@/lib/recipients.functions";
import { selectableSources } from "@/lib/contact-sources";
import {
  CONTACT_CLIENT_CATEGORY_LABELS,
  describeContactFilters,
  type ClientCategory,
  type ContactFilters,
} from "@/lib/contacts";

export const Route = createFileRoute("/_admin/admin/recipients")({
  component: RecipientsPage,
  head: () => ({ meta: [{ title: "Empfängerauswahl – Interner Bereich" }] }),
});

const selectClass = adminInputClass + " pr-8";

function RecipientsPage() {
  const [filters, setFilters] = useState<ContactFilters>({});

  const previewFn = useServerFn(previewRecipients);
  const sourcesFn = useServerFn(listContactSources);
  const tagsFn = useServerFn(listContactTags);

  const sourcesQuery = useQuery({
    queryKey: ["admin", "contact-sources"],
    queryFn: () => sourcesFn(),
    staleTime: 60_000,
  });
  const tagsQuery = useQuery({
    queryKey: ["admin", "contact-tags"],
    queryFn: () => tagsFn(),
    staleTime: 60_000,
  });

  const payload = useMemo(
    () => ({
      sourceId: filters.sourceId ?? null,
      category: filters.category ?? null,
      tag: filters.tag ?? null,
    }),
    [filters],
  );

  const preview = useQuery({
    queryKey: ["admin", "recipients", payload],
    queryFn: () => previewFn({ data: payload }),
  });

  const sources = selectableSources(sourcesQuery.data ?? []);
  const sourceNames = Object.fromEntries(sources.map((s) => [s.id, s.name]));
  const data = preview.data;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Empfängerauswahl"
        description="Stellt einen Empfängerkreis aus der Kontaktliste zusammen und zeigt ihn an. Es wird nichts versendet."
      />

      <div className="grid gap-2 sm:grid-cols-3">
        <select
          aria-label="Nach Kategorie filtern"
          className={selectClass}
          value={filters.category ?? ""}
          onChange={(e) =>
            setFilters((f) => ({ ...f, category: (e.target.value || null) as ClientCategory | null }))
          }
        >
          <option value="">Alle Kategorien</option>
          {(Object.keys(CONTACT_CLIENT_CATEGORY_LABELS) as ClientCategory[]).map((k) => (
            <option key={k} value={k}>
              {CONTACT_CLIENT_CATEGORY_LABELS[k]}
            </option>
          ))}
        </select>

        <select
          aria-label="Nach Herkunft filtern"
          className={selectClass}
          value={filters.sourceId ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, sourceId: e.target.value || null }))}
        >
          <option value="">Alle Herkünfte</option>
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.isActive ? "" : " (deaktiviert)"}
            </option>
          ))}
        </select>

        <select
          aria-label="Nach Schlagwort filtern"
          className={selectClass}
          value={filters.tag ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, tag: e.target.value || null }))}
        >
          <option value="">Alle Schlagworte</option>
          {(tagsQuery.data ?? []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <p className="text-sm text-muted-foreground">
        {describeContactFilters(filters, { sourceNames })}
      </p>

      {preview.isPending ? <p className="text-sm text-muted-foreground">Wird geladen …</p> : null}
      {preview.isError ? (
        <p className="text-sm text-destructive">Die Vorschau ist nicht verfügbar.</p>
      ) : null}

      {data ? (
        <div className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kennzahl label="Empfängerinnen und Empfänger" value={data.total} strong />
            <Kennzahl label="von den Filtern getroffen" value={data.matched} />
            <Kennzahl
              label="ohne Rechtsgrundlage ausgeschlossen"
              value={data.excluded.noLegalBasis}
            />
            <Kennzahl
              label="wegen Widerspruch oder Sperrliste ausgeschlossen"
              value={data.excluded.blocked}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Kennzahl label="davon über Einwilligung" value={data.byLegalBasis.einwilligung} />
            <Kennzahl
              label="davon über Bestandskunden-Ausnahme"
              value={data.byLegalBasis.bestandskunden}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Doppelte Adressen zusammengeführt: {data.excluded.duplicate}. Je Adresse bleibt der
            zuletzt geänderte Kontakt. Widerspruch und Sperrliste schließen unabhängig von der
            Rechtsgrundlage aus.
            {data.complete ? "" : " Hinweis: Der Bestand wurde nicht vollständig geladen."}
          </p>

          <div className="rounded-lg border border-border">
            <div className="border-b border-border px-4 py-3 text-sm font-semibold">
              Stichprobe ({data.sample.length} von {data.total})
            </div>
            <ul className="divide-y divide-border">
              {data.sample.map((r) => (
                <li key={r.email} className="px-4 py-2 text-sm">
                  <span className="font-medium">{r.email}</span>
                  {r.name ? <span className="text-muted-foreground"> · {r.name}</span> : null}
                  {r.organization ? (
                    <span className="text-muted-foreground"> · {r.organization}</span>
                  ) : null}
                </li>
              ))}
              {data.sample.length === 0 ? (
                <li className="px-4 py-3 text-sm text-muted-foreground">
                  Kein Kontakt erfüllt die Bedingungen.
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Kennzahl({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: number;
  strong?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className={strong ? "text-3xl font-semibold text-primary" : "text-2xl font-semibold"}>
        {value}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
